/**
 * useSchoolDetailsWithForm - School Details Update Hook
 *
 * Modern hook using react-hook-form + Zod for school details update form.
 * Replaces useSchoolDetailsForm.ts (manual state management).
 *
 * Features:
 * - react-hook-form for state management
 * - Zod validation via schoolDetailsSchema
 * - shadcn Form components integration
 * - Server-side error handling
 * - Direct API submission (no data transformation!)
 * - Token refresh after update (temporary feature for schoolUpdatedAt)
 *
 * Pattern: Zod schema → react-hook-form → Direct API submission
 *
 * Usage:
 * ```tsx
 * const { form, onSubmit, loading, isDirty } = useSchoolDetailsWithForm();
 *
 * <Form {...form}>
 *   <form onSubmit={onSubmit}>
 *     // ... form fields
 *   </form>
 * </Form>
 * ```
 *
 * Created: 2025-11-02
 * Version: 1.0
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast, Slide } from 'react-toastify';

import { schoolsApi } from '../api/schools.api';
import { useAuthProviderV2 } from '../../../providers/provider-hooks';
import { updateFailureToast, updateSuccessToast } from '../../../toast';
import {
  schoolDetailsSchema,
  type SchoolDetailsFormData,
    type SchoolDetailsFormInput,
} from '../validation/school.schemas';
import { useEffect } from 'react';
import { getErrorMessage } from '@/core/api/response-handler';
import { applyServerFieldErrors } from '@/shared/utils';
import { sanitizeAddress } from '@/shared/utils/sanitize-address';
import { handleInvalidSubmit } from '@/shared/utils';
import { buildSchoolUpdatePayload } from '../validation/build-school-update-payload';

/* =============================================================================
   TYPES
   ============================================================================= */

/* =============================================================================
   HOOK
   ============================================================================= */

export function useSchoolDetailsWithForm() {
  const { handleTokenRefresh } = useAuthProviderV2();
  const queryClient = useQueryClient();

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  // Load existing school data
  // `schoolsApi.getOwn()` trece prin `handleResponse` (arunca real la 4xx/5xx), deci
  // `isError` reflecta corect un 500 -- defectul era ca hook-ul nu-l EXPUNEA, iar
  // pagina randa un formular gol, ca si cum scoala n-ar avea inca date (precedent:
  // use-editions.ts:27-35).
  const { data: schoolData, isLoading: loadingData, isError, refetch } = useQuery({
    queryKey: ['schools', 'own'],
    queryFn: () => schoolsApi.getOwn(),
    retry: false,
  });

  // ============================================================================
  // FORM SETUP
  // ============================================================================

  // Initialize react-hook-form with Zod validation
  const form = useForm<SchoolDetailsFormInput, unknown, SchoolDetailsFormData>({
    resolver: zodResolver(schoolDetailsSchema),
    defaultValues: {
      numeReprezentatLegal: '',
      telefon: '',
      // P1-1 (acelasi verdict ca la useSchoolRequestWithForm.ts): NU 0 --
      // react-hook-form's Controller cade pe DEFAULT-ul inregistrat aici ori
      // de cate ori valoarea curenta e `undefined`, deci golirea campului
      // revenea tacut la 0. Reprodus live (log pe field.value la fiecare
      // randare), nu presupus din citirea codului.
      numarElevi: undefined,
      adresa: {
        judet: '',
        oras: '',
        comuna: '',
        sat: '',
        strada: '',
        numar: '',
        codPostal: '',
        bloc: '',
        scara: '',
        etaj: '',
        apartament: '',
      },
      adresaLivrareAceeasiCuScoala: true,
      adresaLivrare: undefined,
      // Sir gol, nu undefined: `<input type="date">` controlat cere o valoare (acelasi motiv ca P1-1).
      saptamanaVerdeStart: '',
      saptamanaVerdeEnd: '',
      saptamanaVerdeNecunoscuta: false,
    },
    mode: 'onBlur', // Validate on blur for better UX
  });

  // Populate form when data loads
  useEffect(() => {
    if (schoolData) {
      form.reset({
        // `|| ''` acopera si `null`-ul pe care tipul generat nu-l declara.
        numeReprezentatLegal: schoolData.numeReprezentatLegal || '',
        telefon: schoolData.telefon || '',
        // P1-1: `|| 0` ar reinregistra acelasi 0 ca default la fiecare reset.
        numarElevi: schoolData.numarElevi || undefined,
        // `|| {}` acopera doar obiectul null, NU campurile null DIN el (issue #15).
        adresa: sanitizeAddress(schoolData.adresa) || {},
        adresaLivrareAceeasiCuScoala: schoolData.adresaLivrareAceeasiCuScoala ?? true,
        adresaLivrare: sanitizeAddress(schoolData.adresaLivrare),
        // Tipul generat spune `Date`, dar pe sarma e string ISO (LocalDate) -- cast, nu conversie
        // reala; `|| ''` acopera si `null`-ul pe care tipul nu-l declara.
        saptamanaVerdeStart: (schoolData.saptamanaVerdeStart as unknown as string) || '',
        saptamanaVerdeEnd: (schoolData.saptamanaVerdeEnd as unknown as string) || '',
        // UI-only, nu vine din backend (SE-10/11) -- reincarcarea datelor porneste mereu
        // debifata, indiferent ce alesese utilizatorul intr-o sesiune anterioara.
        saptamanaVerdeNecunoscuta: false,
      });
    }
  }, [schoolData, form]);

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  // Update school mutation
  const updateMutation = useMutation({
    mutationFn: async (data: SchoolDetailsFormData) => {
      // 🔴 `id` vine din scoala INCARCATA, nu din formular (issue #15, al patrulea strat).
      // `PATCH /api/schools` n-are parametru de cale: identitatea scolii vine doar din corp, iar
      // `schoolDetailsSchema` listeaza doar campurile editabile — `z.object()` elimina restul,
      // deci `id` disparea tacut de la rescrierea pe Zod. Backendul primea `findById(null)`.
      const payload = buildSchoolUpdatePayload(schoolData, data);
      if (!payload) {
        throw new Error('Datele scolii nu s-au incarcat. Reincarca pagina si incearca din nou.');
      }

      return schoolsApi.update(payload as any);
    },
    onSuccess: async () => {
      // Reset form to clear isDirty flag before any navigation
      // Valorile curente devin noile defaultValues, deci isDirty devine false.
      // NU folosi `reset(undefined, { keepValues: true })`: cu valori omise,
      // react-hook-form pastreaza defaultValues-urile INITIALE si recalculeaza
      // isDirty prin _getDirty() -> ramane true dupa o salvare reusita, iar
      // navigarea de mai jos loveste blocker-ul din useFormNavigation
      // (dialogul "Modificari nesalvate" dupa succes, defect T7/D5).
      form.reset(form.getValues());

      // Token refresh (temporary feature - updates schoolUpdatedAt in JWT)
      await handleTokenRefresh(true);

      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['schools', 'own'] });
    },
  });

  // ============================================================================
  // FORM HANDLERS
  // ============================================================================

  // Form submit handler
  const handleSubmit = async (data: SchoolDetailsFormData) => {
    const toastId = toast.loading('Se salvează...', { transition: Slide });

    try {
      await updateMutation.mutateAsync(data);
      updateSuccessToast('Datele au fost actualizate cu succes!', toastId);
    } catch (error: unknown) {
      console.error('School update error:', error);

      // Handle server-side validation errors
      applyServerFieldErrors(error, form.setError);

      // Show general error message in toast
      const errorMessage = getErrorMessage(error, 'Actualizare eșuată. Verificați datele introduse.');
      updateFailureToast(errorMessage, toastId);
    }
  };

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    form,
    // Al doilea argument nu e optional in practica: fara el, un submit respins de Zod NU
    // executa nimic — dialogul se inchide si utilizatorul ramane fara niciun semnal.
    // Al treilea (SE-07): fallback pentru o eroare de `.refine()` pe o cale fara input
    // randat (0 campuri marcate in DOM nu inseamna 0 erori). Cerut LAZY, ca getter.
    onSubmit: form.handleSubmit(handleSubmit, () =>
      handleInvalidSubmit(undefined, undefined, () => form.formState.errors)
    ),
    loading: updateMutation.isPending || loadingData,
    isDirty: form.formState.isDirty, // Track unsaved changes for navigation blocking
    schoolData, // Expose for read-only fields if needed
    isError,
    refetch,
  };
}
